import { useState } from 'react'
import { useEffect } from 'react'
import { Container } from '@mui/material'
import { Calendar } from 'components/Calendar'
import { useAppSelector } from 'shared/hooks'
import { selectHostServiceDetail } from 'store/hostServiceDetailSlice'
import { DateTime } from 'luxon'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Typography'
import { useReadHostServiceByMonitorIdQuery } from 'services/hostService'
import { useReadMonitorByIdQuery } from 'services/monitor'
import { useDebouncedCallback } from 'use-debounce'
import { isSameDay } from 'shared/lib/isSameDay'

export interface HostServiceDetailAvailabilityProps{
    hostServiceId: number
    spyId?: number
}

export const HostServiceDetailAvailability = (props: HostServiceDetailAvailabilityProps): JSX.Element => {
    const {
        hostServiceId,
        spyId
    } = props

    const [available, setAvailable] = useState<DateTime[]>([])
    const [unavailable, setUnavailable] = useState<DateTime[]>([])
    const [period, setPeriod] = useState<DateTime | undefined>(undefined)
    const [interval, setInterval] = useState<any | undefined>(undefined)

    const [readMonitorByIdQueryParams, setReadMonitorByIdQueryParams] = useState<any>(undefined)
    const [readMonitorByIdOptions, setReadMonitorByIdOptions] = useState<any>(
        {
            skip: true
        }
    )

    const {
        data: readMonitorByIdQueryData,
        isLoading: readMonitorByIdQueryIsLoading
    } = useReadMonitorByIdQuery(readMonitorByIdQueryParams, readMonitorByIdOptions)

    const readMonitorRunResultByMonitorIdQueryOptions = {
        refetchOnMountOrArgChange: true,
        skip: spyId === undefined || interval === undefined
    };

    const readHostServiceByMonitorIdQueryParams: any = {
        monitorId: spyId,
        hostServiceId: hostServiceId,
        firstDay: interval?.startDate.toSQLDate(),
        lastDay: interval?.endDate.toSQLDate(),
    }

    const {
        data: readHostServiceByMonitorIdQueryData,
        isLoading: readHostServiceByMonitorIdQueryDataIsLoading,
        isFetching: readHostServiceByMonitorIdQueryDataIsFetching,
        error: readHostServiceByMonitorIdQueryError
    } = useReadHostServiceByMonitorIdQuery(readHostServiceByMonitorIdQueryParams, readMonitorRunResultByMonitorIdQueryOptions)

    useEffect(() => {
        setReadMonitorByIdQueryParams((prevValue: any) => {
            return {
                ...prevValue,
                monitorId: spyId
            }
        });

        setReadMonitorByIdOptions((prevValue: any) => {
            return {
                    ...prevValue,
                skip: spyId === undefined || interval === undefined,
                }
        })
    }, [
        spyId, 
        interval
    ])


    useEffect(() => {
        if (readMonitorByIdQueryData === undefined) {
            return
        }

        if (readMonitorByIdQueryData.customPeriods && readMonitorByIdQueryData.customPeriods.length > 0) {
            const startDate = DateTime.fromISO(readMonitorByIdQueryData.customPeriods[0].startDateTimeUtc).startOf('day')
            setPeriod(startDate)

            return
        }

        setPeriod(DateTime.now())
    },
        [
            readMonitorByIdQueryData
        ]
    )

        const customDay = (props: PickersDayProps<DateTime>) => {
        const updatedProps = { ...props, selected: false }

        const isAvailableDay = available.some((date) => {
            return isSameDay(date, props.day)
        });

        if (isAvailableDay) {      
            const matchedStyles = { backgroundColor: "#2e7d32", color: '#FFF' }
            return <PickersDay {...updatedProps} sx={{ ...matchedStyles }} />
        }

        const isUnvailableDay = unavailable.some((date) => {
            return isSameDay(date, props.day)
        });

        if (isUnvailableDay) {
            const matchedStyles = { backgroundColor: "#c62828", color: '#FFF' }
            return <PickersDay {...updatedProps} sx={{ ...matchedStyles }} />
        }

        return <PickersDay {...updatedProps} />
    }

    const handleIntervalChangeDebounced = useDebouncedCallback(
        (event: any) => {
            setInterval(event) 
        },
        100
    )

    useEffect(() => {
        if (readHostServiceByMonitorIdQueryData === undefined) {
            return;
        }

        const available: DateTime[] = []
        readHostServiceByMonitorIdQueryData.datesAvailable.forEach((date: any) => {
            available.push(DateTime.fromISO(date))
        });

        const unvailable: DateTime[] = []
        readHostServiceByMonitorIdQueryData.datesUnavailable.forEach((date: any) => {
            unvailable.push(DateTime.fromISO(date))
        });

        setAvailable(available);
        setUnavailable(unvailable)
    }, [
        readHostServiceByMonitorIdQueryData
    ])

    return (
        <>
           <Container>
                <h2>Booking</h2>
                <Calendar
                    loading={readHostServiceByMonitorIdQueryDataIsFetching}
                    onIntervalChange={handleIntervalChangeDebounced}
                    period={period}
                    slots={{
                        calendarHeader: () => (null),
                        day: customDay
                    }}
                />
                <Box component="div">
                    <Box component="ul" sx={{
                        listStyle: 'none',
                        display: 'flex',
                        gap: '10px'
                    } }>
                        <Box component="li" sx={{
                            display: 'flex',
                            alignItems: 'center',
                        }}>
                            <Box sx={{
                                height: '30px',
                                width: '30px',
                                background: '#2e7d32',
                                //border: '2px solid black'
                            }}>
                            </Box>
                            <Typography component="span" sx={{marginLeft: '10px'}}>Available</Typography>
                        </Box>
                        <Box component="li" sx={{
                            display: 'flex',
                            alignItems: 'center',
                        }}>
                            <Box sx={{
                                height: '30px',
                                width: '30px',
                                background: '#c62828',
                                //border: '2px solid black'
                            }}>
                            </Box>
                            <Typography component="span" sx={{ marginLeft: '10px' }}>Booked</Typography>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </>
    )
}
